import { memo, useState, useCallback } from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Box,
  Button,
  Stack,
  Textarea,
  Text,
} from '@chakra-ui/react'
import { useHistory } from 'react-router-dom'

import { Amplitude } from 'utils/amplitude.utils'
import { AppointmentModifyEventProps } from 'utils/amplitude.types'
import { useSchedulingContext } from 'context/SchedulingContext/SchedulingProvider'
import { useAlert } from 'context/AlertContext/AlertContextProvider'
import { RoutePaths } from 'containers/Core/Routes'
import {
  User,
  AppointmentType,
  AppointmentCancellationReason,
} from '@allara-health/source-health-client'
import ChakraRadioGroup from 'components/ChakraRadioGroup'
import ChakraPrimaryButton from 'components/Button/ChakraPrimaryButton'
import CostVerificationModal from './CostVerificationModal'

interface FinalCancelAppointmentModalWithReasonsProps {
  open: boolean
  handleClose: () => void
}

const FinalCancelAppointmentModalWithReasons: React.FC<FinalCancelAppointmentModalWithReasonsProps> = ({
  open,
  handleClose,
}) => {
  const history = useHistory()
  const {
    appointment,
    appointmentType,
    cancelAppointment,
    costConcernAppointment,
    setCurrentScreen,
    setRebookingFlowData,
    provider,
    dateTime,
    timeZone,
  } = useSchedulingContext()
  const { setAlertText } = useAlert()
  const [
    cancellationReason,
    setCancellationReason,
  ] = useState<AppointmentCancellationReason | null>(null)
  const [otherReasonText, setOtherReasonText] = useState<string>('')

  const amplitudeEventProps: AppointmentModifyEventProps = {
    appointment_id: appointment?.id ?? '',
    appointment_type_name: appointmentType?.name ?? '',
    appointment_time: appointment?.start_at ?? '',
  }
  const [isCancelling, setIsCancelling] = useState<boolean>(false)
  const [
    showCostVerificationModal,
    setShowCostVerificationModal,
  ] = useState<boolean>(false)

  const handleCancel = useCallback(() => {
    if (!cancellationReason) {
      return
    }

    if (cancellationReason === 'other' && !otherReasonText.trim()) {
      return
    }

    if (cancellationReason === 'concerned_about_cost') {
      handleClose()
      setShowCostVerificationModal(true)
      return
    }

    Amplitude.appointmentCancelReasonModalClick({
      ...amplitudeEventProps,
      cta: 'cancel',
      code: cancellationReason,
    })
    setIsCancelling(true)

    const cancellationReasonText =
      cancellationReason === 'other' ? otherReasonText : undefined

    cancelAppointment(cancellationReason, cancellationReasonText)
      .then(() => {
        Amplitude.appointmentCancelDone(amplitudeEventProps)
        setRebookingFlowData({
          provider: provider as User,
          appointmentType: appointmentType as AppointmentType,
          dateTime: dateTime as Date,
          timeZone: timeZone as string,
        })
        setCurrentScreen('book')
      })
      .catch(() => {
        setAlertText(
          'Your appointment could not be canceled. Please contact concierge@allarahealth.com for support.',
          'There was an error canceling your appointment'
        )
      })
      .finally(() => {
        handleClose()
        setIsCancelling(false)
        setOtherReasonText('')
      })
  }, [
    cancelAppointment,
    amplitudeEventProps,
    cancellationReason,
    otherReasonText,
    provider,
    appointmentType,
    dateTime,
    timeZone,
    setRebookingFlowData,
    setCurrentScreen,
    setAlertText,
    handleClose,
  ])

  const handleKeepVisit = useCallback(() => {
    setShowCostVerificationModal(false)
    setCancellationReason(null)
    setOtherReasonText('')
    history.push(RoutePaths.DASHBOARD + '?from=scheduling')
  }, [history])

  const handleCancelAnyways = useCallback(() => {
    if (!cancellationReason) {
      return
    }

    setShowCostVerificationModal(false)
    Amplitude.appointmentCancelReasonModalClick({
      ...amplitudeEventProps,
      cta: 'cancel',
      code: cancellationReason,
    })
    setIsCancelling(true)

    cancelAppointment(cancellationReason)
      .then(() => {
        Amplitude.appointmentCancelDone(amplitudeEventProps)
        setRebookingFlowData({
          provider: provider as User,
          appointmentType: appointmentType as AppointmentType,
          dateTime: dateTime as Date,
          timeZone: timeZone as string,
        })
        setCurrentScreen('book')
      })
      .catch(() => {
        setAlertText(
          'Your appointment could not be canceled. Please contact concierge@allarahealth.com for support.',
          'There was an error canceling your appointment'
        )
      })
      .finally(() => {
        handleClose()
        setIsCancelling(false)
        setCancellationReason(null)
        setOtherReasonText('')
      })
  }, [
    cancelAppointment,
    amplitudeEventProps,
    cancellationReason,
    provider,
    appointmentType,
    dateTime,
    timeZone,
    setRebookingFlowData,
    setCurrentScreen,
    setAlertText,
    handleClose,
  ])

  const handleDontCancel = useCallback(
    (cta: 'dont_cancel' | 'close') => {
      Amplitude.appointmentCancelReasonModalClick({
        ...amplitudeEventProps,
        cta,
      })
      handleClose()
      setCancellationReason(null)
    },
    [amplitudeEventProps, handleClose]
  )

  const options: {
    value: AppointmentCancellationReason
    label: string
  }[] = [
    {
      value: 'date_or_time_no_longer_works',
      label: 'Date or time no longer works',
    },
    { value: 'waiting_on_lab_results', label: "I'm waiting on lab results" },
    {
      value: 'user_having_technical_issues',
      label: 'I had trouble with the platform or appointment link',
    },
    {
      value: 'no_longer_interested',
      label: 'I no longer need or want care with Allara',
    },
    {
      value: 'concerned_about_cost',
      label: 'I am concerned about insurance or the cost of the visit',
    },
    { value: 'other', label: 'Other' },
  ]

  return (
    <>
      <Modal
        isOpen={open}
        onClose={() => handleDontCancel('close')}
        isCentered
        size="xs"
      >
        <ModalOverlay />
        <ModalContent maxW="364px">
          <ModalHeader
            borderBottom="none"
            fontFamily="Instrument Serif"
            fontSize="24px"
            fontWeight="400"
            lineHeight="100%"
            pt="20px"
            px="20px"
            pb="16px"
            color="var(--blue-dark)"
          >
            Why do you want to cancel your appointment?
          </ModalHeader>
          <ModalCloseButton mt="7px" fontWeight="400" color="#9091A4" />
          <ModalBody px="20px" pb="20px" pt="0">
            <Box width="100%" mb="16px">
              <ChakraRadioGroup
                options={options}
                value={cancellationReason ?? ''}
                onChange={(value: AppointmentCancellationReason) =>
                  setCancellationReason(value)
                }
              />
            </Box>
            {cancellationReason === 'other' && (
              <Textarea
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                placeholder="Please provide more details"
                size="sm"
                height="80px"
                resize="none"
                borderRadius="8px"
                mb="16px"
              />
            )}
            <Stack spacing={3} width="100%">
              <ChakraPrimaryButton
                onClick={handleCancel}
                variant="dark-blue"
                disabled={
                  isCancelling ||
                  !cancellationReason ||
                  (cancellationReason === 'other' && !otherReasonText.trim())
                }
                isLoading={isCancelling}
              >
                Cancel
              </ChakraPrimaryButton>
              <Button
                variant="outline"
                width="100%"
                height="36px"
                borderRadius="8px"
                borderColor="#141743"
                color="#1F2937"
                onClick={() => handleDontCancel('dont_cancel')}
                fontSize="14px"
                fontWeight="600"
                _hover={{ bg: 'transparent', borderColor: '#9CA3AF' }}
              >
                Nevermind, don&apos;t cancel
              </Button>
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
      <CostVerificationModal
        open={showCostVerificationModal}
        handleClose={() => setShowCostVerificationModal(false)}
        onKeepVisit={handleKeepVisit}
        onCancelAnyways={handleCancelAnyways}
      />
    </>
  )
}

export default memo(FinalCancelAppointmentModalWithReasons)
